import { useCallback } from "react";

/**
 * The filter rows to submit from a screen hosting <CommonReport>, read so that an in-progress cell edit
 * is included.
 *
 * WHY THIS EXISTS. The obvious source is the array `CommonReport` hands back through
 * `onReportStateChange`, and every operation screen used it at first. It is wrong at submit time: when
 * the user types into a filter cell and clicks Start or Preview without tabbing out, DevExtreme has not
 * written the editor's value back to the row yet, so the posted filter silently lacks it. On a screen
 * whose empty-filter path is destructive that is worse than a dropped value — Generate Repeating Misc
 * Charges would offer to replicate the whole company on a filter the user had in fact typed.
 *
 * `CommonReport.getRangeFilters()` is the correct source and already solves it: it awaits
 * `commitPendingGridEdits()`, which calls `saveEditData()` on every filter grid, so the open cell is
 * committed before the rows are read. It also maps flag descriptions back to their codes and drops
 * value-less rows, which is the shape the server's WHERE compilation wants.
 *
 * Returned as a hook rather than a plain function so the three charges operation screens share the
 * fallback and the memoisation too, not just the call — the previous copy-paste was four lines in each.
 *
 * @param {object} reportRef      The same ref passed to <CommonReport innerRef={...}>. May be a ref that
 *                                reaches it indirectly (calculateDeferredRevenue goes through
 *                                CalculateTab's reportRef -> innerRef).
 * @param {Array}  fallbackRows   Rows from `onReportStateChange`, used only when the ref is not wired up
 *                                yet, so a mount race degrades to the old behaviour instead of throwing.
 * @returns {Function} An async function returning the committed filter rows.
 */
export const useCommittedFilterRows = (reportRef, fallbackRows) =>
    useCallback(async () => {
        const report = reportRef?.current;

        if (report && typeof report.getRangeFilters === "function") {
            return await report.getRangeFilters();
        }

        return fallbackRows || [];
    }, [reportRef, fallbackRows]);
